import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { usePathname } from 'next/navigation';
import styles from './subMenu.module.scss';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import trackHeapEvent from '../../../services/helpers/trackHeapEvent';

export default function SubMenu({
  menu = {},
  isOpen = false,
  setIsOpen = () => {},
  onMenuClick,
}) {
  const { width } = useWindowDimensions();
  const ref = useRef();
  const subDropdownRef = useRef(null);
  const [activeMultiSub, setActiveMultiSub] = useState(null);
  const showMultiMenu = (index) => {
    if (activeMultiSub === index) {
      setActiveMultiSub(null);
    } else {
      setActiveMultiSub(index);
    }
  };
  const pathname = usePathname();
  const [isActive, setIsActive] = useState(false);

  const onMouseEnter = () => {
    if (width > 767) {
      setTimeout(() => {
        setIsOpen(true);
      }, 1);
    }
  };

  const onMouseLeave = () => {
    if (width > 767) setIsOpen(false);
  };

  const subMenuHeapTrack = (heapEventKey) => {
    trackHeapEvent(
      { Name: 'Global Nav Header', CTA: heapEventKey },
      'CMS_Unification'
    );
  };

  const handleMenuClick = (heapKey) => {
    subMenuHeapTrack(heapKey.replace(' ', '_'));
    onMenuClick();
    setIsOpen(false);
  };

  const renderSubMenu = (items) => {
    return items?.map((item, index) => {
      const key = `submenu-${index}`;
      return (
        <li aria-hidden="true" key={key} className={`${styles.subList}`}>
          {item?.link ? (
            <Link
              href={item.link}
              data-testid={item.label}
              className={`${pathname === item.link ? styles.activeLink : ''} ${
                item.highlighted ? styles.boldLink : ''
              }`}
              onClick={() => handleMenuClick(item.label)}
            >
              {item.label}
            </Link>
          ) : (
            item?.title && (
              <h5
                className={`${styles.subTitle}`}
                data-testid={item.title}
                onClick={() => showMultiMenu(index)}
                role="presentation"
              >
                {item.title}
              </h5>
            )
          )}
          {item?.items ? (
            <ul
              ref={subDropdownRef}
              className={`${styles.multiList} ${
                activeMultiSub === index ? styles.activeSub : styles.inActiveSub
              }`}
            >
              {renderSubMenu(item.items)}
            </ul>
          ) : null}
        </li>
      );
    });
  };

  useEffect(() => {
    const checkIsActive = (items) => {
      let active = false;
      items.forEach((item) => {
        if (item.link && pathname === item.link) {
          active = true;
        } else if (item.items && checkIsActive(item.items)) {
          active = true;
        }
      });
      return active;
    };

    if (menu?.link && pathname === menu.link) {
      setIsActive(true);
    } else if (menu?.items && checkIsActive(menu.items)) {
      setIsActive(true);
    } else {
      setIsActive(false);
    }
  }, [menu, pathname]);

  return (
    <li
      className={`${styles.navItem} ${isActive ? styles.activeParent : ''} ${
        isOpen ? styles.opened : ''
      }`}
      aria-hidden="true"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {menu?.link ? (
        <Link
          href={menu.link}
          onClick={() => handleMenuClick(menu.title)}
          data-testid={menu.title}
          className={`${pathname === menu.link ? styles.activeLink : ''}`}
        >
          {menu.title}
        </Link>
      ) : (
        menu?.title && (
          <h4
            className={styles.navTitle}
            data-testid={menu.title}
            onClick={() => setIsOpen(!isOpen)}
            role="presentation"
          >
            {menu.title}
          </h4>
        )
      )}
      {isOpen && menu?.items ? (
        <ul
          data-testid="dropdown-menu"
          ref={ref}
          className={`${styles.subMenu}`}
        >
          {renderSubMenu(menu.items)}
        </ul>
      ) : null}
    </li>
  );
}

SubMenu.propTypes = {
  menu: PropTypes.object,
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func,
};
